import { useEffect, useState } from "react";
import { Fingerprint, ScanFace, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/lib/AuthContext";
import { getAccessToken } from "@/lib/api";
import {
  isBiometricAvailable, enableBiometric, hasStoredBiometric, getBiometryLabel,
} from "@/lib/biometric";
import { toast } from "sonner";

const PROMPTED_KEY = "em_biometric_prompted_v1";

/**
 * Shown exactly once after a user's first successful login on a Capacitor (native) device.
 * Offers to enable Face ID / Touch ID for future sessions.
 * Silently no-ops on web.
 */
export default function BiometricEnrollPrompt() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("Face ID");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      if (!user || !user.email) return;
      if (localStorage.getItem(PROMPTED_KEY)) return;
      const { available } = await isBiometricAvailable();
      if (!available) return;
      if (await hasStoredBiometric()) return; // already enrolled
      setLabel(await getBiometryLabel());
      setOpen(true);
    })();
  }, [user]);

  const decline = () => {
    localStorage.setItem(PROMPTED_KEY, "declined");
    setOpen(false);
  };

  const enroll = async () => {
    setBusy(true);
    const token = getAccessToken();
    if (!token) {
      toast.error("Please sign in first.");
      setBusy(false); setOpen(false); return;
    }
    const result = await enableBiometric(token, user.email);
    setBusy(false);
    if (result.ok) {
      localStorage.setItem(PROMPTED_KEY, "enrolled");
      toast.success(`${label} enabled. Next time, open the app and tap to unlock.`);
      setOpen(false);
    } else {
      toast.error(`Couldn't enable ${label}: ${result.reason || "try again"}`);
    }
  };

  if (!open) return null;

  const Icon = label === "Face ID" ? ScanFace : Fingerprint;

  return (
    <AlertDialog open={open} onOpenChange={(o) => !o && decline()}>
      <AlertDialogContent data-testid="biometric-enroll-prompt">
        <AlertDialogHeader>
          <div className="mx-auto w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-2">
            <Icon className="w-7 h-7 text-primary" />
          </div>
          <AlertDialogTitle className="text-center">Enable {label}?</AlertDialogTitle>
          <AlertDialogDescription className="text-center leading-relaxed">
            Skip typing your password next time. Your session is encrypted on-device
            and only unlocks with your {label.toLowerCase()}. You can turn this off
            anytime from Profile → Security.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col gap-2 sm:flex-col">
          <AlertDialogAction
            onClick={enroll}
            disabled={busy}
            className="w-full rounded-full"
            data-testid="biometric-enroll-confirm"
          >
            {busy ? "Enabling…" : `Enable ${label}`}
          </AlertDialogAction>
          <AlertDialogCancel
            onClick={decline}
            className="w-full rounded-full"
            data-testid="biometric-enroll-decline"
          >
            Not now
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
